import { ensureOrgDepartments } from "../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, TextField, Autocomplete, MenuItem, CircularProgress } from "@mui/material";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import {
  createEmployee, addEmployeeToLegalUnit, sendEmployeeOnboardingEmail
} from "../../../../../../services/employee";
import { useNavigate } from "react-router";
import { getOrgEmployees, getOrgDetails, getLegalUnitList, getActiveLegalUnit } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { pressable } from "../../../../../../component/pressableProps";
import { ModalHead, ModalBody, ModalFoot } from "../../../../../../component/Modal";
import { stripEmpty, fieldErrorsFrom, errorMessageFrom } from "../../../../../../utils/formErrors";
import { getOnboardingTemplates } from "../../../../../../services/onboarding";
import {
  STEPS, stepIndex, nextStep, prevStep, isLast, stepErrors, missingForSubmit,
  templateFor, previewChecklist, firstOwedLine, longDate,
} from "./joinerSteps";

import { DateField } from "../../../../../../component/OnboardingFields";
import ErrorMsg from "../../../../../../component/ErrorMsg";
import { tokens } from "../../../../../../theme";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import "../../../../../../styles/PhoneInput.css";

const inputSx = {
  "& .MuiInputBase-root": { fontSize: "0.9375rem", borderRadius: "10px", background: tokens.card },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.border },
};

const Label = ({ children, required }) => (
  <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>
    {children}{required && <span style={{ color: tokens.danger }}> *</span>}
  </Box>
);

// One error presentation for the whole app: see component/ErrorMsg. This used
// to render its own red line, at a different size from the one the shared
// fields use, so two errors on the same form could look like different kinds
// of problem.
const Field = ({ label, required, error, children, width = "48%" }) => (
  <Box sx={{ width: { xs: "100%", sm: width } }}>
    <Label required={required}>{label}</Label>
    {children}
    <ErrorMsg>{error}</ErrorMsg>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mt: "0.5rem", mb: "1rem" }}>
    {children}
  </Box>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The steps, as a tab bar across the top of the modal.
 *
 * Not a stepper: there are no connecting rails and no progress line. Each step
 * is a tab with a numbered disc, and the current one is marked by a 2px rule
 * under it — the same underline the product uses for a selected tab anywhere
 * else, so the flow does not invent a second vocabulary for "you are here".
 *
 * The tabs are clickable. Once a step has been filled in, going back to check
 * something should not mean clicking Back twice and losing your place.
 */
const StepTabs = ({ step, onGo, furthest }) => {
  const at = stepIndex(step);
  return (
    <Stack direction="row" spacing={0} sx={{ px: "28px", borderBottom: `1px solid ${tokens.border}` }}>
      {STEPS.map((s, i) => {
        const done = i < at;
        const current = i === at;
        const reachable = i <= Math.max(at, furthest ?? at);
        return (
          <Stack
            key={s.key}
            direction="row"
            alignItems="center"
            spacing={1.125}
            {...(reachable ? pressable(() => onGo(s.key)) : {})}
            sx={{
              padding: "14px 18px 13px",
              cursor: reachable ? "pointer" : "default",
              boxShadow: current ? `inset 0 -2px 0 ${tokens.primary}` : "none",
            }}
          >
            <Box
              sx={{
                width: "1.25rem", height: "1.25rem", borderRadius: "50%", flex: "none",
                fontSize: "0.6875rem", fontWeight: 700, boxSizing: "border-box",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: done ? tokens.successText : current ? tokens.primary : "transparent",
                color: done || current ? tokens.onPrimary : tokens.textMuted,
                border: done || current ? "none" : `2px solid ${tokens.borderStrong}`,
              }}
            >
              {done ? "✓" : i + 1}
            </Box>
            <Box sx={{ fontSize: "0.875rem", fontWeight: current ? 600 : 400, color: current ? tokens.text : tokens.textMuted, whiteSpace: "nowrap" }}>
              {s.label}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

const ReviewRow = ({ label, value, onEdit }) => (
  <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ py: "0.75rem", borderBottom: `1px solid ${tokens.border}` }}>
    <Box sx={{ width: "10rem", flex: "none", fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted }}>
      {label}
    </Box>
    <Box sx={{ flex: 1, fontSize: "0.875rem", color: tokens.text }}>{value}</Box>
    {onEdit && (
      <Box
        onClick={onEdit}
        sx={{ fontSize: "0.8125rem", color: tokens.primary, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
      >
        Edit
      </Box>
    )}
  </Stack>
);

/**
 * The last step is a review, not a confirmation.
 *
 * The joining date drives every due date on the checklist, and that consequence
 * is invisible while you are picking a date — so the review states how many
 * steps will be issued and which one falls due first.
 */
const Review = ({ form, preview, onEdit }) => {
  const missing = missingForSubmit(form);
  return (
    <Box>
      <SectionTitle>Review</SectionTitle>
      <ReviewRow label="Name" value={`${form.fname} ${form.lname}`.trim() || "—"} onEdit={() => onEdit("personal")} />
      <ReviewRow label="Personal email" value={form.email || "—"} onEdit={() => onEdit("personal")} />
      <ReviewRow label="Role" value={[form.designation, form.department].filter(Boolean).join(" · ") || "—"} onEdit={() => onEdit("job")} />
      <ReviewRow label="Starts" value={longDate(form.joiningDate) || "—"} onEdit={() => onEdit("job")} />
      <ReviewRow label="Onboarding" value={preview?.name || "No template configured"} onEdit={() => onEdit("job")} />
      <ReviewRow
        label="Steps issued"
        value={preview?.count ? `${preview.count} step${preview.count === 1 ? "" : "s"}${preview.requiredCount != null ? `, ${preview.requiredCount} required` : ""}` : "None"}
      />
      <ReviewRow label="First owed step" value={firstOwedLine(preview)} />

      {missing.length > 0 && (
        <Box sx={{ mt: "1.25rem", fontSize: "0.8125rem", color: tokens.danger }}>
          Still needed: {missing.join(", ")}.
        </Box>
      )}

      <Box sx={{ mt: "1.5rem", p: "1rem", borderRadius: "10px", background: tokens.infoTint, fontSize: "0.8125rem", color: tokens.infoText }}>
        An invite email goes to the personal address above so they can set up their
        account. The work account does not exist yet.
      </Box>
    </Box>
  );
};


/**
 * The joiner flow.
 *
 * Rendered inside a modal from the cohort screen: `onCancel` closes it and
 * `onDone` lets the cohort refresh, rather than the form navigating away from a
 * screen it was opened on top of.
 */
export default function EmployeeForm({ onCancel, onDone }) {
  const navigate = useNavigate();
  const notify = useNotify();

  const [form, setForm] = useState({
    fname: "", lname: "", email: "", phone: "",
    designation: "", department: "", reportingTo: "", location: "",
    onboardingType: "", employmentType: "", joiningDate: null,
  });
  // selected option objects (for the autocompletes)
  const [sel, setSel] = useState({ designation: null, department: null, reportingTo: null, location: null });
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [step, setStep] = useState("personal");
  // How far they have got, so a tab already filled in stays clickable.
  const [furthest, setFurthest] = useState(0);
  const [templates, setTemplates] = useState([]);

  // The template only matters on the review step, so a failure to load it
  // costs the preview, not the flow.
  useEffect(() => {
    let active = true;
    getOnboardingTemplates()
      .then((r) => { if (active) setTemplates(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const template = useMemo(() => templateFor(templates, form.onboardingType), [templates, form.onboardingType]);
  const preview = useMemo(() => previewChecklist(template, form.joiningDate), [template, form.joiningDate]);

  // default the legal entity to the active one
  useEffect(() => {
    fetchDepartments();
    const active = getActiveLegalUnit();
    if (active?._id) {
      setSel((p) => ({ ...p, location: { id: active._id, label: active.name } }));
      setForm((p) => ({ ...p, location: active._id }));
    }
    // eslint-disable-next-line
  }, []);

  const fetchDepartments = async () => {
    try {
      const list = await ensureOrgDepartments();
      setDepartments((list || []).map((d) => ({ id: d._id, label: d.name })));
    } catch (e) {
      notify.error(e?.message || "Failed to load departments");
    }
  };

  const employees = (getOrgEmployees() || []).map((e) => ({ id: e._id, label: `${e.fname} ${e.lname}` }));
  const legalUnits = (getLegalUnitList() || []).map((u) => ({ id: u._id, label: u.name }));
  const designations = getOrgDetails()?.designations || [];

  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };
  const setAuto = (key, option) => {
    setSel((p) => ({ ...p, [key]: option }));
    set(key, option?.id || "");
  };

  const validate = () => {
    const e = {};
    if (!form.fname.trim()) e.fname = "First name is required";
    if (!form.lname.trim()) e.lname = "Last name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.phone) e.phone = "Contact number is required";
    if (!form.designation) e.designation = "Designation is required";
    if (!form.department) e.department = "Department is required";
    if (!form.reportingTo) e.reportingTo = "Reporting manager is required";
    if (!form.location) e.location = "Legal entity is required";
    if (!form.onboardingType) e.onboardingType = "Onboarding type is required";
    if (!form.joiningDate) e.joiningDate = "Joining date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      notify.error("Please fix the highlighted fields");
      return;
    }
    setLoading(true);
    try {
      // Blank optionals are dropped rather than sent as "": the API's
      // validator rejects an empty string for an optional field, so leaving
      // "Employment type" untouched used to produce a 400.
      const res = await createEmployee(stripEmpty(form));
      if (res.success) {
        const employeeId = res.data._id;
        await addEmployeeToLegalUnit(employeeId);
        // The employee is created at this point. The invite email is a secondary
        // step — if it fails, still treat onboarding as done (the invite can be
        // resent from the directory) and move forward, rather than stranding the
        // admin on the form (which invites a duplicate submission).
        const sendMail = await sendEmployeeOnboardingEmail(employeeId);
        if (sendMail.success) {
          notify.success("Employee onboarded — invite sent");
        } else {
          notify.warning("Employee added, but the invite email couldn't be sent. You can resend it from the directory.");
        }
        sessionStorage.setItem("employeeId", employeeId);
        if (onDone) onDone(employeeId);
        else navigate("/app/employees/onboarding-invite");
      } else {
        // Point at the field rather than at the form. A generic "fill in the
        // required fields", with nothing highlighted, sends an admin hunting
        // through a form that is as far as it knows complete.
        const serverErrors = fieldErrorsFrom(res);
        if (Object.keys(serverErrors).length) setErrors((prev) => ({ ...prev, ...serverErrors }));
        notify.error(errorMessageFrom(res, "Could not onboard employee"));
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong onboarding the employee");
    } finally {
      setLoading(false);
    }
  };

  const autoInput = (key, error) => (params) => (
    <TextField {...params} placeholder="Select" sx={inputSx} error={!!error} />
  );

  return (
    <>
      <ModalHead
        title="Onboard employee"
        subtitle="They will get an invite to set up their own account."
        onClose={onCancel}
      />
      <StepTabs step={step} onGo={setStep} furthest={furthest} />

      <ModalBody>
      {step === "personal" && (<>
      <SectionTitle>Personal details</SectionTitle>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: "1.25rem 4%" }}>
        <Field label="First name" required error={errors.fname}>
          <TextField fullWidth size="small" placeholder="First name" value={form.fname}
            onChange={(e) => set("fname", e.target.value)} error={!!errors.fname} sx={inputSx} />
        </Field>
        <Field label="Last name" required error={errors.lname}>
          <TextField fullWidth size="small" placeholder="Last name" value={form.lname}
            onChange={(e) => set("lname", e.target.value)} error={!!errors.lname} sx={inputSx} />
        </Field>
        <Field label="Personal email" required error={errors.email}>
          <TextField fullWidth size="small" placeholder="name@example.com" value={form.email}
            onChange={(e) => set("email", e.target.value)} error={!!errors.email} sx={inputSx} />
        </Field>
        <Field label="Contact number" required error={errors.phone}>
          {/* The wrapper is the control: it draws the border, the radius and
              the background, and the inner input is text on it — see
              styles/PhoneInput.css. Height and font match the MUI fields
              beside it so the row does not look assembled from two kits. */}
          <Box
            sx={{
              "& .PhoneInput": {
                display: "flex",
                alignItems: "center",
                height: "2.5rem",
                border: `1px solid ${errors.phone ? tokens.danger : tokens.border}`,
                borderRadius: "10px",
                padding: "0 0.75rem",
                background: tokens.card,
                transition: "border-color 120ms cubic-bezier(0.2, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.2, 0, 0.2, 1)",
              },
            }}
          >
            <PhoneInput
              international
              className={errors.phone ? "PhoneInput--error" : undefined}
              value={form.phone}
              onChange={(v) => set("phone", v || "")}
            />
          </Box>
        </Field>
      </Stack>

      </>)}

      {step === "job" && (<>
      <SectionTitle>Job details</SectionTitle>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: "1.25rem 4%" }}>
        <Field label="Designation" required error={errors.designation}>
          <Autocomplete options={designations} size="small" value={form.designation || null}
            onChange={(e, v) => set("designation", v || "")} renderInput={autoInput("designation", errors.designation)} />
        </Field>
        <Field label="Department" required error={errors.department}>
          <Autocomplete options={departments} size="small" value={sel.department}
            onChange={(e, v) => setAuto("department", v)} renderInput={autoInput("department", errors.department)} />
        </Field>
        <Field label="Reporting manager" required error={errors.reportingTo}>
          <Autocomplete options={employees} size="small" value={sel.reportingTo}
            onChange={(e, v) => setAuto("reportingTo", v)} renderInput={autoInput("reportingTo", errors.reportingTo)} />
        </Field>
        <Field label="Legal entity" required error={errors.location}>
          <Autocomplete options={legalUnits} size="small" value={sel.location}
            onChange={(e, v) => setAuto("location", v)} renderInput={autoInput("location", errors.location)} />
        </Field>
        <Field label="Employment type" error={errors.employmentType}>
          <TextField select fullWidth size="small" value={form.employmentType}
            onChange={(e) => set("employmentType", e.target.value)} sx={inputSx}>
            {["Full-time", "Part-time", "Internship", "Contract"].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Field>
        <Field label="Onboarding type" required error={errors.onboardingType}>
          <TextField select fullWidth size="small" value={form.onboardingType}
            onChange={(e) => set("onboardingType", e.target.value)} error={!!errors.onboardingType} sx={inputSx}>
            {["Remote", "Office"].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        </Field>
        <Box sx={{ width: { xs: "100%", sm: "48%" } }}>
          <DateField
            label="Date of joining *"
            value={form.joiningDate}
            isDialogOpen={dateOpen}
            openDialog={() => setDateOpen(true)}
            closeDialog={() => setDateOpen(false)}
            validation={errors.joiningDate}
            onSelect={(date) => { set("joiningDate", date); setDateOpen(false); }}
          />
        </Box>
      </Stack>

      </>)}

      {step === "review" && <Review form={form} preview={preview} onEdit={setStep} />}

      </ModalBody>

      {/* Nothing is created until the last step, so the button that creates it
          is the only one that says so. */}
      <ModalFoot note="Three steps. Nothing is created until the last one.">
        {step === "personal" ? (
          <BtnSecondary onClick={onCancel} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
        ) : (
          <BtnSecondary onClick={() => setStep(prevStep(step))} sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        )}
        {isLast(step) ? (
          <BtnPrimary onClick={submit} disabled={loading || missingForSubmit(form).length > 0} sx={{ borderRadius: "8px" }}>
            {loading ? <CircularProgress size={22} sx={{ color: tokens.onPrimary }} /> : "Onboard employee"}
          </BtnPrimary>
        ) : (
          <BtnPrimary
            onClick={() => {
              const errs = stepErrors(form, step);
              if (Object.keys(errs).length) { setErrors((p) => ({ ...p, ...errs })); return; }
              const to = nextStep(step);
              setFurthest((f) => Math.max(f, stepIndex(to)));
              setStep(to);
            }}
            sx={{ borderRadius: "8px" }}
          >
            Continue
          </BtnPrimary>
        )}
      </ModalFoot>
    </>
  );
}
